/**
 * ELF-CIRCLE — Recognition.
 *
 * The company overview is direct about this: "award-winning" has to be built,
 * not asserted, and the page should ship seeded even while the case is empty.
 * So it does — the shelf is honest about holding nothing yet, and the value of
 * the page is the calendar underneath it: which circuits, which windows, and
 * which existing work is eligible for each.
 *
 * Signature mechanism: the submission year. Four circuits sit on a twelve-month
 * track as their real submission windows; selecting one slides the marker and
 * swaps the eligibility read beneath, so the page functions as an internal
 * operating calendar as much as an external signal.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import filmFestival from '../../../../../assets/elf/film-festival.png';
import autumnArt from '../../../../../assets/elf/season-autumn.jpg';
import springArt from '../../../../../assets/elf/season-spring.jpg';
import summerArt from '../../../../../assets/elf/season-summer.jpg';
import winterArt from '../../../../../assets/elf/season-winter.jpg';

interface Circuit {
  id: string;
  name: string;
  /** Card label on the awards shelf. */
  short: string;
  what: string;
  /** Submission window as month indices, 0-based and inclusive. */
  from: number;
  to: number;
  window: string;
  /** Why this circuit, given the portfolio that already exists. */
  fit: string;
  /** Work already in the portfolio that would carry a submission. */
  eligible: string[];
}

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

/** The season each circuit's window falls in — it backs the submission year. */
const SEASON_ART: Record<string, string> = {
  cannes: winterArt, // January — April
  adcolor: springArt, // March — June
  effie: summerArt, // May — September
  oneshow: autumnArt, // September — December
};

const CIRCUITS: Circuit[] = [
  {
    id: 'adcolor',
    short: 'ADCOLOR',
    name: 'ADCOLOR',
    what: 'Industry recognition for creative talent and work of colour.',
    from: 2,
    to: 5,
    window: 'March — June',
    fit: 'The circuit closest to the firm’s own thesis: built from culture rather than assigned to it. A multicultural agency of record makes its case here first.',
    eligible: ['Label-side artist rollouts', 'Made In New Jersey Festival', 'Off-Broadway campaigns'],
  },
  {
    id: 'cannes',
    short: 'Cannes Lions',
    name: 'Cannes Lions — Entertainment & Music',
    what: 'The Entertainment and Music Lions specifically, not the general-market categories.',
    from: 0,
    to: 3,
    window: 'January — April',
    fit: 'Music and film work is judged against its own craft here, which suits a portfolio built on artist rollouts and festival-adjacent film marketing rather than brand films.',
    eligible: ['Drop-day activation campaigns', 'Tribeca distributor-direct titles'],
  },
  {
    id: 'effie',
    short: 'Effie Awards',
    name: 'Effie Awards — Multicultural',
    what: 'Effectiveness, judged on results, in the multicultural categories.',
    from: 4,
    to: 8,
    window: 'May — September',
    fit: 'Effies are scored on proof rather than craft, which rewards the ROI discipline behind the media buys instead of the artwork on top of them.',
    eligible: ['Media buy & ROI strategy engagements', 'Independent artist campaigns'],
  },
  {
    id: 'oneshow',
    short: 'The One Show',
    name: 'The One Show',
    what: 'Craft and idea, with strong independent and cultural representation.',
    from: 8,
    to: 11,
    window: 'September — December',
    fit: 'The closing window of the year, and the one where an owned property competes well — a festival the firm originated is a different entry from a campaign it was hired for.',
    eligible: ['Made In New Jersey Festival', 'Monthly networking series'],
  },
];

/**
 * Where a circuit's submission window stands this month: open now, or how many
 * months until it opens (wrapping into next year). Months are 0-based.
 */
function windowStatus(c: Circuit, month: number): { label: string; open: boolean } {
  if (month >= c.from && month <= c.to) return { label: 'Window open now', open: true };
  const wait = c.from > month ? c.from - month : 12 - month + c.from;
  return { label: `Opens in ${wait} month${wait === 1 ? '' : 's'}`, open: false };
}

export const Recognition: React.FC = () => {
  const consult = useConsult();
  const [at, setAt] = useState(0);
  const circuit = CIRCUITS[at];
  /** Read once — the shelf's status only needs the month the page was opened in. */
  const [month] = useState(() => new Date().getMonth());
  const yearRef = useRef<HTMLDivElement>(null);

  /** A shelf card selects its circuit in the year below and brings it into view. */
  const openCircuit = (i: number) => {
    setAt(i);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    yearRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/recognition" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Recognition']} />
          </div>

          {/* --- the honest shelf --- */}
          <div className="rec-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                <i />
                Built, not asserted
              </span>
              <h1 className="elf-page__h1">
                The case is empty.
                <br />
                <span style={{ color: 'var(--acc-panel)' }}>On purpose.</span>
              </h1>
              <p className="wbody" style={{ maxWidth: '58ch' }}>
                &ldquo;Award-winning&rdquo; is a claim a firm earns on a calendar, not a line it
                writes into its own copy. Submissions are a standing business goal here, scheduled
                annually against the circuits that actually reward cultural and independent work.
                When something lands, it lands on this shelf.
              </p>
            </div>
            {/* The awards shelf: one slot per circuit, each still an empty mount.
                Status is live against the current month, and a slot opens that
                circuit in the submission year below. */}
            <div className="rec-shelf">
              <div className="rec-shelf__head">
                <span className="statl">Awards shelf</span>
                <span className="rec-shelf__tally">0 of 4 won yet</span>
              </div>
              <div className="rec-shelf__slots">
                {CIRCUITS.map((c, i) => {
                  const status = windowStatus(c, month);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className="rec-slot"
                      aria-pressed={i === at}
                      data-open={status.open ? 'true' : undefined}
                      onClick={() => openCircuit(i)}
                    >
                      <span className="rec-slot__mount" aria-hidden="true" />
                      <span className="rec-slot__name">{c.short}</span>
                      <span className="rec-slot__window">{c.window}</span>
                      <span className="rec-slot__status">{status.label}</span>
                    </button>
                  );
                })}
              </div>
              <span className="rec-shelf__hint">
                <span className="hint-hover">Click</span>
                <span className="hint-touch">Tap</span> a circuit to see its window and eligible work
              </span>
            </div>
          </div>

          {/* --- signature: the submission year --- */}
          <div className="rec-year" ref={yearRef}>
            {/* One picture per circuit, all mounted; the selected one is faded in. */}
            <span className="rec-year__art" aria-hidden="true">
              {CIRCUITS.map((c) => (
                <img key={c.id} src={SEASON_ART[c.id]} alt="" data-on={c.id === circuit.id ? 'true' : undefined} />
              ))}
              <span />
            </span>
            <div className="rec-year__head">
              <span className="colhead">The submission year</span>
              <h2 className="elf-page__h2">Four circuits, calendared</h2>
              <p className="wbody" style={{ maxWidth: '60ch' }}>
                Not a general-market sweep — the circuits chosen are the ones that reward cultural
                and independent work specifically, which is the work this firm actually makes.
              </p>
            </div>

            <div className="rec-track">
              <div className="rec-track__months" aria-hidden="true">
                {MONTHS.map((m, i) => (
                  <span key={`${m}${i}`} data-in={i >= circuit.from && i <= circuit.to ? 'true' : undefined}>
                    {m}
                  </span>
                ))}
              </div>
              <div className="rec-track__bands">
                {CIRCUITS.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    className="rec-band"
                    aria-pressed={i === at}
                    onClick={() => setAt(i)}
                    onMouseEnter={() => setAt(i)}
                    style={
                      {
                        '--from': String(c.from),
                        '--span': String(c.to - c.from + 1),
                      } as React.CSSProperties
                    }
                  >
                    <span className="rec-band__name">{c.name}</span>
                    <span className="rec-band__win">{c.window}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rec-detail" key={circuit.id}>
              <div>
                <span className="statl">Why this circuit</span>
                <p className="wbody" style={{ fontSize: 15.5, marginTop: 10 }}>
                  {circuit.fit}
                </p>
                <p className="wtag" style={{ fontSize: 13, marginTop: 14 }}>
                  {circuit.what}
                </p>
              </div>
              <div className="rec-elig">
                <span className="statl">Eligible work today</span>
                <ul>
                  {circuit.eligible.map((e) => (
                    <li key={e}>
                      <b>✓</b>
                      {e}
                    </li>
                  ))}
                </ul>
                <Link className="pillghost" to={route('/portfolio')} style={{ marginTop: 6 }}>
                  See the portfolio →
                </Link>
              </div>
            </div>
          </div>

          {/* --- the standing goal --- */}
          <div className="rec-goal">
            <span className="rec-goal__art">
              <img src={filmFestival} alt="" />
              <span />
            </span>
            <div className="rec-goal__copy">
              <span className="colhead">A standing business goal</span>
              <h2 className="elf-page__h2" style={{ marginTop: 10 }}>
                Submissions are a line item, not an afterthought.
              </h2>
              <p className="wbody" style={{ maxWidth: '56ch', marginTop: 14 }}>
                Every circuit above is calendared annually, with the eligible work identified before
                the window opens rather than during it. Clients whose engagements are award-eligible
                are told so at kickoff — a submission is a shared outcome, not something a firm does
                quietly with someone else&rsquo;s campaign.
              </p>
              <button
                type="button"
                className="pillcta mag"
                style={{ marginTop: 22, alignSelf: 'flex-start' }}
                onClick={() =>
                  consult?.open({
                    source: 'recognition.submission',
                    title: 'Award-eligible engagement',
                  })
                }
              >
                Talk about an eligible engagement <span>→</span>
              </button>
            </div>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/portfolio')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">The work</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Portfolio
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  What a submission would carry.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <Link className="nextlink" to={route('/why-elf-circle')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Pillar 04</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  The competitive case
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Recognition, pursued deliberately.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default Recognition;
